import React, { useEffect, useState } from 'react'
import { View, Text, TextInput } from 'react-native'
import { MapPin, Search, SearchX } from '@/components/ui/icons'
import { useTranslation } from 'react-i18next'
import { Pressable } from '../ui/Pressable'
import { AnimatedSpinner } from '../ui/AnimatedSpinner'
import { GlassSurface } from '../ui/GlassSurface'
import { useSemanticColors, withAlpha, RADIUS, SPACING } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { WEB_SHADOWS } from '../../lib/theme/web-shadow'
import { COLORS, FONTS } from '../../lib/brand'
import type { UserLocation } from '../../lib/types/vehicle.types'

const VIVID_ACTION_COLOR = COLORS.coral.DEFAULT
const CLEAR_ACTION_COLOR = COLORS.purple.DEFAULT
const SEARCH_BAR_MIN_HEIGHT = 58
const SEARCH_FIELD_MIN_HEIGHT = 46
const SEARCH_ICON_SIZE = 34
const SEARCH_BUTTON_SIZE = 48

interface SearchBlockProps {
  location: UserLocation | null
  onLocationPress: () => void
  /** Submits the search. Receives the committed free-text query as typed
   *  (trimmed already by the input); the location scope and filters are read
   *  from their own state. */
  onSubmit: (query: string) => void
  variant?: 'mobile' | 'desktop'
  resultCount: number
  isLoading?: boolean
  /** The committed query (from the store). Reset/clear edits sync back here. */
  query: string
}

export function SearchBlock({
  location,
  onLocationPress,
  onSubmit,
  variant = 'mobile',
  resultCount,
  isLoading,
  query,
}: SearchBlockProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const desktop = variant === 'desktop'

  // The input edits a local draft; the search only commits it on submit. This
  // keeps a keystroke from triggering a network search per character while
  // still giving submit the exact text the user saw. A clear/reset on the
  // store side syncs the draft back down.
  const [draft, setDraft] = useState(query)
  useEffect(() => {
    setDraft(query)
  }, [query])

  const busy = Boolean(isLoading)
  const hasNoResults = resultCount === 0 && !busy
  // With no location this used to read "San Salvador, El Salvador" — a city
  // nobody chose, printed as if they had. The field's job is to let someone
  // narrow the search, so when nothing is chosen it invites instead of
  // asserting. Which scope is active is stated once, by the pills below.
  const locationLabel = location?.name ?? t('explore.default_location')
  const resultLabel = busy
    ? t('explore.searching')
    : hasNoResults
      ? t('explore.no_results')
      : t('explore.result_count', { count: resultCount })

  const commit = () => {
    onSubmit(draft.trim())
  }

  const clearQuery = () => {
    setDraft('')
    onSubmit('')
  }

  const hasQuery = draft.trim().length > 0 || query.trim().length > 0

  return (
    <GlassSurface
      tone="regular"
      visualRole="chrome"
      radius={RADIUS.xl}
      style={{
        minHeight: desktop ? SEARCH_BAR_MIN_HEIGHT - 2 : SEARCH_BAR_MIN_HEIGHT,
        width: '100%',
      }}
      contentStyle={{ padding: desktop ? 6 : 7 }}
      testID="explore.search-bar"
    >
      <View style={{ gap: SPACING.sm }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
          <Pressable
            onPress={onLocationPress}
            accessibilityRole="button"
            accessibilityLabel={t('explore.search_where')}
            accessibilityHint={resultLabel}
            testID="explore.location-button"
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: SEARCH_FIELD_MIN_HEIGHT,
              borderRadius: RADIUS.lg,
              paddingHorizontal: desktop ? SPACING.md : SPACING.sm + 1,
              paddingVertical: 7,
              // A controlled recessed layer: transparent enough to pick up
              // the material above it, still dense enough for a text field to
              // read as an input instead of decoration.
              backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.48)',
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.72)',
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
            }}
          >
            <View
              style={{
                width: SEARCH_ICON_SIZE,
                height: SEARCH_ICON_SIZE,
                borderRadius: RADIUS.full,
                backgroundColor: withAlpha(COLORS.coral.DEFAULT, isDark ? 0.18 : 0.1),
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MapPin size={17} color={COLORS.coral.DEFAULT} strokeWidth={2.3} />
            </View>
            <Text
              testID="explore.location-label"
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: desktop ? 16 : 16,
                lineHeight: 21,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.78}
            >
              {locationLabel}
            </Text>
          </Pressable>

          <Pressable
            onPress={commit}
            accessibilityRole="button"
            accessibilityLabel={t('explore.search_cta')}
            accessibilityHint={resultLabel}
            accessibilityState={{ busy }}
            testID="explore.search-submit"
            style={({ pressed }) => ({
              width: SEARCH_BUTTON_SIZE,
              height: SEARCH_BUTTON_SIZE,
              borderRadius: RADIUS.lg,
              backgroundColor: VIVID_ACTION_COLOR,
              borderWidth: 1,
              borderColor: 'rgba(255,255,255,0.36)',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: pressed ? 0.9 : 1,
              ...WEB_SHADOWS.sm,
            })}
          >
            {/* A re-search resolves with the previous list still on screen, so the
                button itself has to say "working" — otherwise tapping search looks
                like it did nothing at all. */}
            {busy ? (
              <AnimatedSpinner size={22} color="#FFFFFF" testID="explore.search-submit-spinner" />
            ) : (
              <Search size={22} color="#FFFFFF" strokeWidth={2.3} />
            )}
          </Pressable>
        </View>

        {/* Free-text: "Vitara", "cross", a year… The RPC already matched title and
            make/model by ILIKE since the schema's first marketplace; it was only
            never reachable from this bar. It sits on its own row so the draft can
            stay wider than a phone's narrow search row. The purple clear is the
            48px twin of the coral lupa, stacked under it — query only. */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
          <View
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: SEARCH_FIELD_MIN_HEIGHT,
              borderRadius: RADIUS.lg,
              paddingHorizontal: desktop ? SPACING.md : SPACING.sm + 1,
              backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.48)',
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.72)',
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
            }}
          >
            <Search size={17} color={colors.textMuted} strokeWidth={2.3} />
            <TextInput
              testID="explore.search-input"
              value={draft}
              onChangeText={setDraft}
              onSubmitEditing={commit}
              returnKeyType="search"
              autoCorrect={false}
              autoCapitalize="none"
              clearButtonMode="never"
              placeholder={t('explore.search_placeholder')}
              placeholderTextColor={colors.textMuted}
              accessibilityLabel={t('explore.search_placeholder')}
              style={{
                flex: 1,
                minWidth: 0,
                paddingVertical: 0,
                fontSize: 15,
                lineHeight: 21,
                fontFamily: FONTS.body,
                color: colors.textPrimary,
              }}
            />
          </View>

          {hasQuery ? (
            <Pressable
              onPress={clearQuery}
              accessibilityRole="button"
              accessibilityLabel={t('explore.clear_search')}
              testID="explore.search-clear"
              style={({ pressed }) => ({
                width: SEARCH_BUTTON_SIZE,
                height: SEARCH_BUTTON_SIZE,
                borderRadius: RADIUS.lg,
                backgroundColor: CLEAR_ACTION_COLOR,
                borderWidth: 1,
                borderColor: 'rgba(255,255,255,0.28)',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: pressed ? 0.9 : 1,
                ...WEB_SHADOWS.sm,
              })}
            >
              <SearchX size={22} color="#FFFFFF" strokeWidth={2.3} />
            </Pressable>
          ) : null}
        </View>
      </View>
    </GlassSurface>
  )
}
